import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tab,
  Tabs,
  Alert,
  Avatar,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  DirectionsCar as CarIcon,
  AccessTime as TimeIcon,
  CheckCircle,
  Business as DepartmentIcon,
} from '@mui/icons-material';

import EnhancedRequestForm from '../components/EnhancedRequestForm';
import ApprovalDashboard from '../components/ApprovalDashboard';
import RequestStatusTracking from '../components/RequestStatusTracking';
import VehicleCalendarScheduler from '../components/VehicleCalendarScheduler';
import { SupervisorApprovalDialog, FleetApprovalDialog, RejectionDialog } from '../components/ApprovalDialogs';
import { useAuth } from '../contexts/AuthContext';
import { vehicleRequestAPI, vehicleAPI } from '../services/api';
import type { VehicleRequest, FleetVehicle } from '../types';

interface RequestFormData {
  purpose: string;
  destination: string;
  startDateTime: Date;
  endDateTime: Date;
  passengerCount: number;
  vehicleCategory: string;
  specialRequirements: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

const VehicleRequestsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [requests, setRequests] = useState<VehicleRequest[]>([]);
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [myRequests, setMyRequests] = useState<VehicleRequest[]>([]);
  const [pendingApprovalRequests, setPendingApprovalRequests] = useState<VehicleRequest[]>([]);
  const [openNewRequest, setOpenNewRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // Dialog states
  const [supervisorApprovalDialog, setSupervisorApprovalDialog] = useState<{
    open: boolean;
    requestId: string | null;
    requestDetails: any;
  }>({ open: false, requestId: null, requestDetails: null });

  const [fleetApprovalDialog, setFleetApprovalDialog] = useState<{
    open: boolean;
    requestId: string | null;
    requestDetails: any;
    availableVehicles: FleetVehicle[];
  }>({ open: false, requestId: null, requestDetails: null, availableVehicles: [] });

  const [rejectionDialog, setRejectionDialog] = useState<{
    open: boolean;
    requestId: string | null;
    requestDetails: any;
    rejectionType: 'supervisor' | 'fleet';
  }>({ open: false, requestId: null, requestDetails: null, rejectionType: 'supervisor' });
  
  const [stats, setStats] = useState({
    pending: 0,
    approvedToday: 0,
    availableVehicles: 0,
    activeDepartments: 0
  });

  const { user, hasPermission } = useAuth();
  const canApprove = hasPermission('write:devices') || user?.role === 'fleet_manager' || user?.role === 'super_admin';

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        loadRequests(),
        loadVehicles(),
        loadMyRequests(),
        loadPendingApprovals(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      const response = await vehicleRequestAPI.getAllRequests({ page: 1, limit: 100 });
      setRequests(response.data);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  const loadVehicles = async () => {
    try {
      const vehicleData = await vehicleAPI.getAllVehicles({ page: 1, limit: 100 });
      setVehicles(vehicleData);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const loadMyRequests = async () => {
    try {
      const myRequestData = await vehicleRequestAPI.getMyRequests();
      setMyRequests(myRequestData);
    } catch (error) {
      console.error('Error loading my requests:', error);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      if (canApprove) {
        const supervisorRequests = await vehicleRequestAPI.getPendingSupervisorRequests();
        const fleetRequests = await vehicleRequestAPI.getPendingFleetRequests();
        setPendingApprovalRequests([...supervisorRequests, ...fleetRequests]);
      }
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    }
  };

  const loadStats = async () => {
    try {
      const requestStats = await vehicleRequestAPI.getRequestStats('30d');
      const vehicleData = await vehicleAPI.getAllVehicles({ status: 'available' });
      
      const activeDepartments = new Set(
        requests
          .filter(r => r.requestedBy && typeof r.requestedBy === 'object' && 'department' in r.requestedBy)
          .map(r => (r.requestedBy as any).department)
          .filter(Boolean)
      ).size;

      setStats({
        pending: requestStats.pendingRequests,
        approvedToday: requestStats.approvedRequests,
        availableVehicles: vehicleData.length,
        activeDepartments
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSubmitRequest = async (formData: RequestFormData) => {
    try {
      setLoading(true);
      await vehicleRequestAPI.createRequest({
        purpose: formData.purpose,
        destination: formData.destination,
        startDateTime: formData.startDateTime.toISOString(),
        endDateTime: formData.endDateTime.toISOString(),
        passengerCount: formData.passengerCount,
        vehicleCategory: formData.vehicleCategory === 'any' ? undefined : formData.vehicleCategory as any,
        specialRequirements: formData.specialRequirements || undefined,
        priority: formData.priority
      });
      await loadInitialData();
      setSuccessMessage('Request submitted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error submitting request:', error);
      setError(error.response?.data?.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  // ===== APPROVAL HANDLERS =====
  const handleOpenSupervisorApproval = async (requestId: string) => {
    const request = requests.find(r => r._id === requestId) || pendingApprovalRequests.find(r => r._id === requestId);
    if (!request) return;

    const requesterName = typeof request.requestedBy === 'object' && 'firstName' in request.requestedBy
      ? `${(request.requestedBy as any).firstName} ${(request.requestedBy as any).lastName}`
      : 'Unknown User';

    setSupervisorApprovalDialog({
      open: true,
      requestId,
      requestDetails: {
        purpose: request.purpose,
        destination: request.destination,
        requester: requesterName,
        passengerCount: request.passengerCount
      }
    });
  };

  const handleSupervisorApprove = async (comments: string) => {
    if (!supervisorApprovalDialog.requestId) return;
    
    try {
      setLoading(true);
      await vehicleRequestAPI.supervisorApprove(supervisorApprovalDialog.requestId, comments);
      setSupervisorApprovalDialog({ open: false, requestId: null, requestDetails: null });
      await loadInitialData();
      setSuccessMessage('Request approved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error approving request:', error);
      setError(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFleetApproval = async (requestId: string) => {
    const request = requests.find(r => r._id === requestId) || pendingApprovalRequests.find(r => r._id === requestId);
    if (!request) return;
  
    try {
      setLoading(true);
      const startDate = typeof request.startDateTime === 'string' ? request.startDateTime : request.startDateTime.toISOString();
      const endDate = typeof request.endDateTime === 'string' ? request.endDateTime : request.endDateTime.toISOString();
      
      const availableVehiclesData = await vehicleRequestAPI.getAvailableVehicles(
        startDate,
        endDate,
        request.vehicleCategory
      );
  
      // ✅ Map to FleetVehicle format (they should already be this format from backend)
      const availableVehicles: FleetVehicle[] = availableVehiclesData.map((v: any) => ({
        id: v._id || v.id,
        vin: v.vin,
        plateNumber: v.plateNumber,
        category: v.category,
        make: v.make,
        model: v.model,
        year: v.year,
        color: v.color,
        fuelType: v.fuelType,
        assignedDepartment: v.assignedDepartment,
        assignedRegion: v.assignedRegion,
        assignedDriver: v.assignedDriver,
        deviceId: v.deviceId,
        deviceBound: v.deviceBound || false,
        isActive: v.isActive,
        lastMaintenanceDate: v.lastMaintenanceDate,
        nextMaintenanceDate: v.nextMaintenanceDate,
        insuranceExpiry: v.insuranceExpiry,
        registrationExpiry: v.registrationExpiry,
        purchaseDate: v.purchaseDate,
        purchasePrice: v.purchasePrice,
        currentMileage: v.currentMileage,
        fuelCapacity: v.fuelCapacity,
        seatingCapacity: v.seatingCapacity,
        createdAt: v.createdAt,
        updatedAt: v.updatedAt,
      }));
  
      const requesterName = typeof request.requestedBy === 'object' && 'firstName' in request.requestedBy
        ? `${(request.requestedBy as any).firstName} ${(request.requestedBy as any).lastName}`
        : 'Unknown User';
  
      setFleetApprovalDialog({
        open: true,
        requestId,
        requestDetails: {
          purpose: request.purpose,
          destination: request.destination,
          requester: requesterName,
          passengerCount: request.passengerCount,
          startDate,
          endDate,
          category: request.vehicleCategory
        },
        availableVehicles
      });
    } catch (error: any) {
      console.error('Error loading available vehicles:', error);
      setError('Failed to load available vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleFleetApprove = async (vehicleId: string, driverName: string, comments: string) => {
    if (!fleetApprovalDialog.requestId) return;
    
    try {
      setLoading(true);
      await vehicleRequestAPI.fleetApprove(
        fleetApprovalDialog.requestId,
        vehicleId,
        driverName,
        comments
      );
      setFleetApprovalDialog({ open: false, requestId: null, requestDetails: null, availableVehicles: [] });
      await loadInitialData();
      setSuccessMessage('Vehicle assigned successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error assigning vehicle:', error);
      setError(error.response?.data?.message || 'Failed to assign vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRejection = (requestId: string, rejectionType: 'supervisor' | 'fleet') => {
    const request = requests.find(r => r._id === requestId) || pendingApprovalRequests.find(r => r._id === requestId);
    if (!request) return;

    const requesterName = typeof request.requestedBy === 'object' && 'firstName' in request.requestedBy
      ? `${(request.requestedBy as any).firstName} ${(request.requestedBy as any).lastName}`
      : 'Unknown User';

    setRejectionDialog({
      open: true,
      requestId,
      requestDetails: {
        purpose: request.purpose,
        requester: requesterName
      },
      rejectionType
    });
  };

  const handleReject = async (reason: string) => {
    if (!rejectionDialog.requestId) return;
    
    try {
      setLoading(true);
      if (rejectionDialog.rejectionType === 'supervisor') {
        await vehicleRequestAPI.supervisorReject(rejectionDialog.requestId, reason);
      } else {
        await vehicleRequestAPI.fleetReject(rejectionDialog.requestId, reason);
      }
      setRejectionDialog({ open: false, requestId: null, requestDetails: null, rejectionType: 'supervisor' });
      await loadInitialData();
      setSuccessMessage('Request rejected');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      setError(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  if (loading && requests.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3,
          gap: 2
        }}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold' }}>
          Vehicle Request Portal
        </Typography>
        <Button
          variant="contained"
          startIcon={!isMobile && <AddIcon />}
          onClick={() => setOpenNewRequest(true)}
          disabled={loading}
          size={isMobile ? 'small' : 'medium'}
          fullWidth={isMobile}
        >
          {isMobile ? 'New' : 'New Request'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}><TimeIcon /></Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.pending}</Typography>
                  <Typography variant="body2" color="text.secondary">Pending Requests</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}><CheckCircle /></Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.approvedToday}</Typography>
                  <Typography variant="body2" color="text.secondary">Approved Today</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}><CarIcon /></Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.availableVehicles}</Typography>
                  <Typography variant="body2" color="text.secondary">Available Vehicles</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}><DepartmentIcon /></Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.activeDepartments}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Departments</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab label="My Requests" />
          {canApprove && <Tab label="Pending Approvals" />}
          <Tab label={isMobile ? 'Calendar' : 'Calendar & Availability'} />
        </Tabs>

        <CardContent>
          {activeTab === 0 && <RequestStatusTracking />}
          {activeTab === 1 && canApprove && (
            <ApprovalDashboard 
              requests={pendingApprovalRequests}
              onApprove={(id, type) => {
                if (type === 'supervisor') handleOpenSupervisorApproval(id);
                else handleOpenFleetApproval(id);
              }}
              onReject={(id, type) => handleOpenRejection(id, type)}
              userRole={user?.role as any}
            />
          )}
          {activeTab === (canApprove ? 2 : 1) && <VehicleCalendarScheduler />}
        </CardContent>
      </Card>

      <EnhancedRequestForm
        open={openNewRequest}
        onClose={() => setOpenNewRequest(false)}
        onSubmit={handleSubmitRequest}
      />

      <SupervisorApprovalDialog
        open={supervisorApprovalDialog.open}
        onClose={() => setSupervisorApprovalDialog({ open: false, requestId: null, requestDetails: null })}
        onApprove={handleSupervisorApprove}
        requestDetails={supervisorApprovalDialog.requestDetails || {}}
        loading={loading}
      />

      <FleetApprovalDialog
        open={fleetApprovalDialog.open}
        onClose={() => setFleetApprovalDialog({ open: false, requestId: null, requestDetails: null, availableVehicles: [] })}
        onApprove={handleFleetApprove}
        requestDetails={fleetApprovalDialog.requestDetails || {}}
        availableVehicles={fleetApprovalDialog.availableVehicles}
        loading={loading}
      />

      <RejectionDialog
        open={rejectionDialog.open}
        onClose={() => setRejectionDialog({ open: false, requestId: null, requestDetails: null, rejectionType: 'supervisor' })}
        onReject={handleReject}
        requestDetails={rejectionDialog.requestDetails || {}}
        loading={loading}
      />
    </Box>
  );
};

export default VehicleRequestsPage;